import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { notify } from '../../helpers/utils';
import {
  User, Phone, MapPin, Globe, Building, CheckCircle,
  Loader, AlertTriangle
} from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg: 'var(--nd-bg)', surface: 'var(--nd-surface)', surf2: 'var(--nd-surface-2)',
  border: 'var(--nd-w08)', text: 'var(--nd-text)', muted: 'var(--nd-tx35)',
  blue: '#3B82F6', teal: '#22D3A5', amber: '#FBBF24', red: '#F87171',
  purple: '#A78BFA', font: "'DM Sans',sans-serif", mono: "'DM Mono',monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin   { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  .ec-input {
    width:100%; padding:9px 12px 9px 34px; height:40px;
    background:var(--nd-w05); border:1px solid var(--nd-w10);
    border-radius:9px; color:var(--nd-text); font-size:13px; font-family:'DM Sans',sans-serif;
    outline:none; transition:border-color 0.15s,background 0.15s;
  }
  .ec-input::placeholder { color:var(--nd-tx22); }
  .ec-input:focus { border-color:rgba(59,130,246,0.55); background:rgba(59,130,246,0.05); }
  .ec-label { display:block; font-size:10px; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--nd-tx40); margin-bottom:5px; }
  .ec-btn { display:inline-flex; align-items:center; gap:7px; padding:10px 24px; border-radius:9px; border:none; background:linear-gradient(135deg,#3B82F6,#22D3A5); color:var(--nd-text-strong); font-family:'DM Sans',sans-serif; font-size:13px; font-weight:700; cursor:pointer; box-shadow:0 4px 14px rgba(59,130,246,0.28); transition:opacity 0.15s; }
  .ec-btn:hover:not(:disabled) { opacity:.88; }
  .ec-btn:disabled { opacity:.45; cursor:not-allowed; }
  ::-webkit-scrollbar{width:4px;height:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:var(--nd-w10);border-radius:4px}
`;

const Field = ({ label, icon: Icon, iconColor = T.muted, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label className="ec-label">{label}</label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={13} color={iconColor} style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',zIndex:1 }} />}
      {children}
    </div>
  </div>
);

const Grid2 = ({ children }) => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'0 20px' }}>{children}</div>
);

// ══════════════════════════════════════════════════════════════════
const EditCustomer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/customers/${id}`);
        setCustomer(res.data);
        console.log(res.data);
        setLoading(false);
      } catch (err) {
        notify('error', `Error fetching customers: ${err.response?.data?.message || err.message}`);
        console.error('Fetch Error:', err);
        setError('Failed to fetch customer.');
        setLoading(false);
      }
    };
    fetchCustomer();
  }, [id]);

  const handleChange = (e) => {
    setCustomer({ ...customer, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/customers/${id}`, customer);
      navigate('/sales/search-customers');
    } catch (err) {
      notify('error', `Error updating customer: ${err.response?.data?.message || err.message}`);
      console.error('Update Error:', err);
      setError('Failed to update customer.');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div style={{ background:T.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:'80px 20px', gap:12 }}>
        <Loader size={20} color={T.blue} style={{ animation:'spin 0.8s linear infinite' }} />
        <span style={{ color:T.muted, fontFamily:T.font, fontSize:13 }}>Loading customer…</span>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ background:T.bg, fontFamily:T.font, color:T.text, padding:'14px 20px' }}>
      <style>{STYLES}</style>

      {/* Page header */}
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:16, marginBottom:18, overflow:'hidden' }}>
        <div style={{ padding:'18px 24px' }}>
          <div style={{ fontSize:9, color:T.muted, letterSpacing:'0.18em', marginBottom:5 }}>NOVAA · SALES</div>
          <div style={{ fontSize:26, fontWeight:800, color:'var(--nd-text-strong)', letterSpacing:'-0.03em', lineHeight:1 }}>Edit Customer</div>
          <div style={{ fontSize:12, color:T.muted, marginTop:5 }}>Update details for Customer #{id}</div>
        </div>
        <div style={{ height:2, background:'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ background:'rgba(248,113,113,0.10)',border:'1px solid rgba(248,113,113,0.28)',borderRadius:10,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:9,color:T.red,fontSize:13 }}>
          <AlertTriangle size={15} /> {error}
        </div>
      )}

      {customer && (
        <form onSubmit={handleSubmit}>
          {/* Personal info */}
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:14, overflow:'hidden', marginBottom:16, animation:'fadeUp 0.3s ease both' }}>
            <div style={{ padding:'12px 18px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:9 }}>
              <div style={{ width:26,height:26,borderRadius:7,background:'rgba(59,130,246,0.12)',border:'1px solid rgba(59,130,246,0.25)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                <User size={13} color={T.blue} />
              </div>
              <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Personal Information</span>
            </div>
            <div style={{ padding:'18px 20px 4px' }}>
              <Grid2>
                <Field label="Client Name" icon={User} iconColor={T.blue}>
                  <input className="ec-input" type="text" name="client_name" value={customer.client_name||''} onChange={handleChange} required />
                </Field>
                <Field label="Phone Number" icon={Phone} iconColor={T.teal}>
                  <input className="ec-input" type="text" name="phone_number" value={customer.phone_number||''} onChange={handleChange} required />
                </Field>
              </Grid2>
              <Field label="Address" icon={MapPin} iconColor={T.amber}>
                <input className="ec-input" type="text" name="address" value={customer.address||''} onChange={handleChange} />
              </Field>
            </div>
          </div>

          {/* Location */}
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:14, overflow:'hidden', marginBottom:16, animation:'fadeUp 0.3s ease 0.06s both' }}>
            <div style={{ padding:'12px 18px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:9 }}>
              <div style={{ width:26,height:26,borderRadius:7,background:'rgba(34,211,165,0.12)',border:'1px solid rgba(34,211,165,0.25)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                <Globe size={13} color={T.teal} />
              </div>
              <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Location Details</span>
            </div>
            <div style={{ padding:'18px 20px 4px' }}>
              <Grid2>
                <Field label="City" icon={Building} iconColor={T.teal}>
                  <input className="ec-input" type="text" name="client_city" value={customer.client_city||''} onChange={handleChange} />
                </Field>
                <Field label="State" icon={MapPin} iconColor={T.purple}>
                  <input className="ec-input" type="text" name="client_state" value={customer.client_state||''} onChange={handleChange} />
                </Field>
                <Field label="Country" icon={Globe} iconColor={T.blue}>
                  <input className="ec-input" type="text" name="client_country" value={customer.client_country||''} onChange={handleChange} />
                </Field>
              </Grid2>
            </div>
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end', paddingBottom:4 }}>
            <button type="submit" className="ec-btn" disabled={saving}>
              {saving
                ? <><Loader size={13} style={{ animation:'spin 0.8s linear infinite' }} /> Saving…</>
                : <><CheckCircle size={13} /> Update Customer</>}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EditCustomer;